/**
 * External dependencies
 */
import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  YAxis,
  LabelList,
} from "recharts";
import { get, size } from "lodash";
import { CustomTooltip } from "../custom-tooltip";
/**
 * Internal dependencies
 */
import { COLORS, COMPARISON_COLORS } from "./constants";
import {
  toYAxisFormatter,
  shouldColorCells,
  formatResultSetToShortTitle,
  toNormalizedSeriesNames,
  toNormalizedResultData,
  isInFirstHalf,
} from "./utils";
import EmptyContent from "./base/empty-content";
import { CustomLegend } from "../custom-legend";

export default ({
  resultSet,
  stacked,
  colorTheme,
  rowTotals = false,
  hideLegend = false,
  axisTickAngle = 0,
  compare,
}) => {
  let formatter = toYAxisFormatter(resultSet);

  let data = toNormalizedResultData(resultSet);

  if (size(data) === 0) {
    return <EmptyContent />;
  }

  const normalizedSeries = toNormalizedSeriesNames(resultSet);

  const colorCells = React.useMemo(() => {
    return shouldColorCells(resultSet);
  }, []);

  if (colorCells) {
    data = data.map((d, i) => ({
      ...d,
      fill: get(COLORS, [colorTheme || "default", i]),
    }));
  }

  return (
    <ResponsiveContainer
      width="100%"
      height="100%"
      className="chart__bar-chart"
    >
      <BarChart
        data={data}
        margin={{ top: 20, right: 40, bottom: 10, left: 15 }}
        layout="vertical"
      >
        <CartesianGrid stroke="#eee" horizontal={false} />
        <Tooltip dataKey="" content={CustomTooltip} formatter={formatter} />
        {hideLegend ? null : (
          <Legend
            verticalAlign="top"
            align="left"
            iconType="circle"
            wrapperStyle={{ paddingBottom: 25 }}
            iconSize={10}
            content={(p) => <CustomLegend {...p} isCompare={compare} />}
          />
        )}
        <XAxis
          allowDataOverflow={false}
          stroke="#7c98b6"
          tick={{ fill: "#33475b", fontSize: 10 }}
          domain={[0, 0]}
          tickLine={false}
          axisLine={false}
          height={25}
          type="number"
          tickFormatter={formatter}
        />
        <YAxis
          tickLine={false}
          dataKey="x"
          type="category"
          tick={{ fill: "#33475b", fontSize: 10 }}
          angle={axisTickAngle}
        ></YAxis>
        {normalizedSeries.map((series, i) => {
          const isFirstHalf = isInFirstHalf(normalizedSeries, i);

          const colors =
            compare === true
              ? isFirstHalf
                ? COLORS
                : COMPARISON_COLORS
              : COLORS;

          return (
            <Bar
              key={series.key}
              stackId={stacked && "a"}
              dataKey={series.key}
              name={formatResultSetToShortTitle(resultSet.queryType, series)}
              fill={get(colors, [colorTheme || "default", i])}
              fillOpacity={!compare || isFirstHalf ? 1 : 0.4}
            >
              {rowTotals && <LabelList dataKey={series.key} position="right" />}
            </Bar>
          );
        })}
      </BarChart>
    </ResponsiveContainer>
  );
};
